import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import CRMTenantBar from './CRMTenantBar';

const API = '/api';
const PRIMARY = '#6366f1';
const SUCCESS = '#22c55e';
const DANGER = '#ef4444';
const WARNING = '#f59e0b';
const BORDER = '#e5e7eb';
const BG = '#f8fafc';
const CARD = '#ffffff';
const TEXT = '#1e293b';
const MUTED = '#64748b';

function hdr() { return { Authorization: `Bearer ${localStorage.getItem('token')}` }; }

const STATUS_COLORS: Record<string, string> = {
  connected: SUCCESS, disconnected: MUTED, expired: WARNING, error: DANGER,
};
const PROVIDERS = [
  { key: 'salesforce', name: 'Salesforce', logo: '☁', desc: 'Contacts, Leads, Cases, Opportunities, Activities', environments: ['production', 'sandbox'], regions: [] as string[] },
  { key: 'zoho', name: 'Zoho CRM', logo: '🔷', desc: 'Contacts, Leads, Accounts, Deals, Activities', environments: [] as string[], regions: ['us', 'eu', 'in', 'au', 'cn'] },
];

interface Tenant { id: number; company_name?: string; }
interface Connection { id: number; provider_key: string; status: string; environment: string; region: string; connected_user: string; connected_at: string; last_health_check_at: string; last_latency_ms: number; last_health_status: string; token_expires_at: string; error_message: string; has_config: boolean; }
interface ConfigForm { connection_name: string; client_id: string; client_secret: string; environment: string; region: string; }

export default function CRMIntegrationsPage({ user, tenants = [] }: { user: any; tenants?: Tenant[] }) {
  const isSuperAdmin = !!(user?.role === 'super_admin' || user?.is_admin);
  const [selectedTenantId, setSelectedTenantId] = useState<number | null>(
    isSuperAdmin ? (tenants?.[0]?.id ?? null) : (user?.tenant_id ?? null)
  );
  const tenantId = isSuperAdmin ? selectedTenantId : (user?.tenant_id ?? null);
  const qs = tenantId !== null ? `?tenant_id=${tenantId}` : '';

  const [connections, setConnections] = useState<Connection[]>([]);
  const [loading, setLoading] = useState(false);
  const [configOpen, setConfigOpen] = useState<string | null>(null);
  const [testResult, setTestResult] = useState<any>(null);
  const [testingId, setTestingId] = useState<number | null>(null);
  const [form, setForm] = useState<ConfigForm>({ connection_name: 'Default', client_id: '', client_secret: '', environment: 'production', region: 'us' });
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState<{ text: string; type: 'ok' | 'err' } | null>(null);

  const load = useCallback(async () => {
    if (tenantId === null) { setConnections([]); return; }
    setLoading(true);
    try {
      const r = await axios.get(`${API}/crm-integration/connections${qs}`, { headers: hdr() });
      setConnections(r.data.connections || []);
    } catch (e: any) {
      setMsg({ text: e?.response?.data?.error || 'Failed to load', type: 'err' });
    } finally { setLoading(false); }
  }, [tenantId, qs]);

  useEffect(() => { load(); setTestResult(null); setMsg(null); }, [load]);

  const getConn = (key: string) => connections.find(c => c.provider_key === key);

  const openConfig = (key: string) => {
    const c = getConn(key);
    setForm({ connection_name: 'Default', client_id: '', client_secret: '', environment: c?.environment || 'production', region: c?.region || 'us' });
    setConfigOpen(key);
    setMsg(null);
  };

  const saveConfig = async () => {
    if (!form.client_id.trim()) { setMsg({ text: 'Client ID is required', type: 'err' }); return; }
    if (tenantId === null) { setMsg({ text: 'Select a tenant first', type: 'err' }); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/crm-integration/connections`, { provider_key: configOpen, tenant_id: tenantId, ...form }, { headers: hdr() });
      setMsg({ text: 'Configuration saved', type: 'ok' });
      await load();
      setTimeout(() => setConfigOpen(null), 1000);
    } catch (e: any) {
      setMsg({ text: e?.response?.data?.error || 'Save failed', type: 'err' });
    } finally { setSaving(false); }
  };

  const connect = async (key: string) => {
    const c = getConn(key);
    if (!c) { setMsg({ text: 'Save configuration first', type: 'err' }); return; }
    try {
      const r = await axios.post(`${API}/crm-integration/connections/${c.id}/connect${qs}`, {}, { headers: hdr() });
      if (r.data.auth_url) {
        window.open(r.data.auth_url, 'crm_oauth', 'width=700,height=600,scrollbars=yes');
        setTimeout(() => load(), 3000);
        setTimeout(() => load(), 8000);
      }
    } catch (e: any) { setMsg({ text: e?.response?.data?.error || 'Connect failed', type: 'err' }); }
  };

  const disconnect = async (key: string) => {
    const c = getConn(key);
    if (!c || !window.confirm(`Disconnect ${key}?`)) return;
    try {
      await axios.post(`${API}/crm-integration/connections/${c.id}/disconnect${qs}`, {}, { headers: hdr() });
      setMsg({ text: 'Disconnected', type: 'ok' });
      await load();
    } catch (e: any) { setMsg({ text: 'Disconnect failed', type: 'err' }); }
  };

  const testConn = async (conn: Connection) => {
    setTestingId(conn.id); setTestResult(null);
    try {
      const r = await axios.post(`${API}/crm-integration/connections/${conn.id}/test${qs}`, {}, { headers: hdr() });
      setTestResult({ ...r.data, connId: conn.id });
    } catch (e: any) {
      setTestResult({ healthy: false, error: e?.response?.data?.error || 'Test failed', connId: conn.id });
    } finally { setTestingId(null); }
  };

  const refreshToken = async (conn: Connection) => {
    try {
      await axios.post(`${API}/crm-integration/connections/${conn.id}/refresh-token${qs}`, {}, { headers: hdr() });
      setMsg({ text: 'Token refreshed', type: 'ok' }); await load();
    } catch { setMsg({ text: 'Refresh failed', type: 'err' }); }
  };

  const formatDate = (d: string) => d ? new Date(d).toLocaleString() : '—';
  const tokenExpiry = (conn: Connection) => {
    if (!conn.token_expires_at) return null;
    const mins = Math.round((new Date(conn.token_expires_at).getTime() - Date.now()) / 60000);
    if (mins < 0) return { label: 'Expired', color: DANGER };
    if (mins < 30) return { label: `${mins}m`, color: WARNING };
    if (mins < 1440) return { label: `${Math.round(mins / 60)}h`, color: SUCCESS };
    return { label: `${Math.round(mins / 1440)}d`, color: SUCCESS };
  };

  const s: Record<string, React.CSSProperties> = {
    page: { padding: 24, background: BG, minHeight: '100vh' },
    title: { fontSize: 22, fontWeight: 700, color: TEXT, margin: '0 0 4px' },
    sub: { color: MUTED, fontSize: 14, marginBottom: 20 },
    grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(420px, 1fr))', gap: 20 },
    card: { background: CARD, borderRadius: 12, border: `1px solid ${BORDER}`, overflow: 'hidden' },
    cardHead: { padding: '16px 20px', borderBottom: `1px solid ${BORDER}`, display: 'flex', alignItems: 'center', gap: 12 },
    logo: { width: 44, height: 44, borderRadius: 10, background: BG, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22, flexShrink: 0 },
    provName: { fontSize: 17, fontWeight: 700, color: TEXT },
    provDesc: { fontSize: 12, color: MUTED, marginTop: 2 },
    badge: { marginLeft: 'auto', padding: '3px 10px', borderRadius: 20, fontSize: 12, fontWeight: 600, flexShrink: 0 },
    cardBody: { padding: '16px 20px' },
    row: { display: 'flex', justifyContent: 'space-between', fontSize: 13, color: MUTED, marginBottom: 6 },
    rowVal: { color: TEXT, fontWeight: 500, marginLeft: 8, textAlign: 'right' as const },
    actions: { padding: '12px 20px', borderTop: `1px solid ${BORDER}`, display: 'flex', gap: 8, flexWrap: 'wrap' as const },
    btn: { padding: '7px 14px', borderRadius: 7, border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 600 },
    overlay: { position: 'fixed' as const, inset: 0, background: 'rgba(0,0,0,.45)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' },
    modal: { background: CARD, borderRadius: 14, padding: 28, width: 480, maxWidth: '95vw', maxHeight: '90vh', overflowY: 'auto' as const },
    label: { fontSize: 13, fontWeight: 600, color: TEXT, display: 'block', marginBottom: 4 },
    input: { width: '100%', padding: '9px 12px', borderRadius: 8, border: `1px solid ${BORDER}`, fontSize: 14, color: TEXT, background: CARD, boxSizing: 'border-box' as const },
    select: { width: '100%', padding: '9px 12px', borderRadius: 8, border: `1px solid ${BORDER}`, fontSize: 14, color: TEXT, background: CARD },
    formRow: { marginBottom: 14 },
    alert: { padding: '10px 14px', borderRadius: 8, fontSize: 13, marginBottom: 14 },
    testBox: { background: BG, borderRadius: 8, padding: 14, marginTop: 12, fontSize: 13 },
  };

  const selTenantName = isSuperAdmin ? tenants.find(t => t.id === tenantId)?.company_name : null;

  return (
    <div style={s.page}>
      <h2 style={s.title}>CRM Integrations</h2>
      <p style={s.sub}>Connect Salesforce and Zoho CRM to automatically log calls, pop screens, and sync data — per tenant.</p>

      <CRMTenantBar
        isSuperAdmin={isSuperAdmin}
        tenants={tenants}
        selectedTenantId={selectedTenantId}
        onSelect={(id) => { setSelectedTenantId(id); setTestResult(null); setMsg(null); }}
      />

      {msg && (
        <div style={{ ...s.alert, background: msg.type === 'ok' ? '#f0fdf4' : '#fef2f2', color: msg.type === 'ok' ? '#166534' : '#991b1b', border: `1px solid ${msg.type === 'ok' ? '#86efac' : '#fca5a5'}` }}>
          {msg.text}
        </div>
      )}

      {tenantId === null ? (
        <div style={{ textAlign: 'center', padding: 60, color: MUTED }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>🏢</div>
          <div style={{ fontSize: 16, fontWeight: 600 }}>Select a tenant to manage their CRM connections</div>
        </div>
      ) : loading ? (
        <div style={{ textAlign: 'center', padding: 40, color: MUTED }}>Loading{selTenantName ? ` ${selTenantName}'s` : ''} CRM connections...</div>
      ) : (
        <div style={s.grid}>
          {PROVIDERS.map(prov => {
            const conn = getConn(prov.key);
            const status = conn?.status || 'not_configured';
            const statusColor = STATUS_COLORS[status] || MUTED;
            const expiry = conn ? tokenExpiry(conn) : null;
            const tr = testResult?.connId === conn?.id ? testResult : null;
            return (
              <div key={prov.key} style={s.card}>
                <div style={s.cardHead}>
                  <div style={s.logo}>{prov.logo}</div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={s.provName}>{prov.name}</div>
                    <div style={s.provDesc}>{prov.desc}</div>
                  </div>
                  <span style={{ ...s.badge, background: statusColor + '20', color: statusColor }}>
                    {status === 'not_configured' ? 'Not Configured' : status.charAt(0).toUpperCase() + status.slice(1)}
                  </span>
                </div>
                <div style={s.cardBody}>
                  {conn ? (
                    <>
                      <div style={s.row}><span>Environment</span><span style={s.rowVal}>{conn.environment || '—'}</span></div>
                      {conn.region && <div style={s.row}><span>Region</span><span style={s.rowVal}>{conn.region.toUpperCase()}</span></div>}
                      <div style={s.row}><span>Connected User</span><span style={s.rowVal}>{conn.connected_user || '—'}</span></div>
                      <div style={s.row}><span>Connected At</span><span style={s.rowVal}>{formatDate(conn.connected_at)}</span></div>
                      {conn.last_health_check_at && (
                        <div style={s.row}><span>Last Health</span><span style={{ ...s.rowVal, color: conn.last_health_status === 'healthy' ? SUCCESS : DANGER }}>{conn.last_health_status} · {conn.last_latency_ms}ms</span></div>
                      )}
                      {expiry && <div style={s.row}><span>Token Expires</span><span style={{ ...s.rowVal, color: expiry.color }}>{expiry.label}</span></div>}
                      {conn.error_message && <div style={{ fontSize: 12, color: DANGER, marginTop: 6, background: '#fef2f2', padding: '6px 10px', borderRadius: 6 }}>{conn.error_message}</div>}
                      {tr && (
                        <div style={s.testBox}>
                          <div style={{ fontWeight: 700, color: tr.healthy ? SUCCESS : DANGER, marginBottom: 6 }}>{tr.healthy ? '✓ Connection Healthy' : '✗ Connection Failed'}</div>
                          {tr.user && <div style={s.row}><span>Auth User</span><span style={s.rowVal}>{tr.user}</span></div>}
                          {tr.api_version && <div style={s.row}><span>API Version</span><span style={s.rowVal}>{tr.api_version}</span></div>}
                          {tr.latency_ms && <div style={s.row}><span>Latency</span><span style={s.rowVal}>{tr.latency_ms}ms</span></div>}
                          {tr.error && <div style={{ color: DANGER, fontSize: 12 }}>{tr.error}</div>}
                        </div>
                      )}
                    </>
                  ) : (
                    <div style={{ color: MUTED, fontSize: 13, padding: '8px 0' }}>No CRM connected for this tenant. Configure OAuth credentials below.</div>
                  )}
                </div>
                <div style={s.actions}>
                  <button style={{ ...s.btn, background: 'transparent', color: PRIMARY, border: `1px solid ${PRIMARY}` }} onClick={() => openConfig(prov.key)}>
                    {conn?.has_config ? 'Edit Config' : 'Configure'}
                  </button>
                  {conn && conn.status !== 'connected' && (
                    <button style={{ ...s.btn, background: PRIMARY, color: '#fff' }} onClick={() => connect(prov.key)}>Connect OAuth</button>
                  )}
                  {conn && conn.status === 'connected' && (
                    <>
                      <button style={{ ...s.btn, background: BG, color: TEXT, border: `1px solid ${BORDER}` }} onClick={() => testConn(conn)} disabled={testingId === conn.id}>
                        {testingId === conn.id ? 'Testing...' : 'Test'}
                      </button>
                      <button style={{ ...s.btn, background: BG, color: TEXT, border: `1px solid ${BORDER}` }} onClick={() => refreshToken(conn)}>Refresh Token</button>
                      <button style={{ ...s.btn, background: 'transparent', color: DANGER, border: `1px solid ${DANGER}` }} onClick={() => disconnect(prov.key)}>Disconnect</button>
                    </>
                  )}
                  {conn && conn.status === 'expired' && (
                    <button style={{ ...s.btn, background: PRIMARY, color: '#fff' }} onClick={() => connect(prov.key)}>Re-Authenticate</button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {configOpen && (
        <div style={s.overlay}>
          <div style={s.modal}>
            <h3 style={{ margin: '0 0 4px', color: TEXT }}>Configure {PROVIDERS.find(p => p.key === configOpen)?.name}</h3>
            {isSuperAdmin && selTenantName && <p style={{ margin: '0 0 18px', fontSize: 13, color: MUTED }}>For tenant: <strong>{selTenantName}</strong></p>}
            {msg && <div style={{ ...s.alert, background: msg.type === 'ok' ? '#f0fdf4' : '#fef2f2', color: msg.type === 'ok' ? '#166534' : '#991b1b' }}>{msg.text}</div>}
            <div style={s.formRow}>
              <label style={s.label}>Connection Name</label>
              <input style={s.input} value={form.connection_name} onChange={e => setForm(f => ({ ...f, connection_name: e.target.value }))} />
            </div>
            <div style={s.formRow}>
              <label style={s.label}>OAuth Client ID *</label>
              <input style={s.input} value={form.client_id} onChange={e => setForm(f => ({ ...f, client_id: e.target.value }))} placeholder="Connected App Client ID" />
            </div>
            <div style={s.formRow}>
              <label style={s.label}>OAuth Client Secret *</label>
              <input style={s.input} type="password" value={form.client_secret} onChange={e => setForm(f => ({ ...f, client_secret: e.target.value }))} placeholder="Client Secret" />
            </div>
            {(PROVIDERS.find(p => p.key === configOpen)?.environments?.length ?? 0) > 0 && (
              <div style={s.formRow}>
                <label style={s.label}>Environment</label>
                <select style={s.select} value={form.environment} onChange={e => setForm(f => ({ ...f, environment: e.target.value }))}>
                  {PROVIDERS.find(p => p.key === configOpen)!.environments.map(env => (
                    <option key={env} value={env}>{env.charAt(0).toUpperCase() + env.slice(1)}</option>
                  ))}
                </select>
              </div>
            )}
            {(PROVIDERS.find(p => p.key === configOpen)?.regions?.length ?? 0) > 0 && (
              <div style={s.formRow}>
                <label style={s.label}>Data Centre Region</label>
                <select style={s.select} value={form.region} onChange={e => setForm(f => ({ ...f, region: e.target.value }))}>
                  {PROVIDERS.find(p => p.key === configOpen)!.regions.map(r => (
                    <option key={r} value={r}>{r.toUpperCase()}</option>
                  ))}
                </select>
              </div>
            )}
            <div style={{ fontSize: 12, color: MUTED, background: BG, padding: 10, borderRadius: 8, marginBottom: 16 }}>
              <strong>OAuth Redirect URI</strong><br />
              <code style={{ fontSize: 11 }}>/api/crm-integration/oauth/callback</code>
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button style={{ ...s.btn, background: BG, color: TEXT, border: `1px solid ${BORDER}` }} onClick={() => setConfigOpen(null)}>Cancel</button>
              <button style={{ ...s.btn, background: PRIMARY, color: '#fff' }} onClick={saveConfig} disabled={saving}>{saving ? 'Saving...' : 'Save & Continue'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
